import React, { useCallback, useContext, useMemo, useRef, useState, useEffect } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { genderMap, roleMap } from "../../data/userColumn";
import { useApiClient } from "../../context/ApiClientContext";
import { useNavigate } from "react-router-dom";
import { DataContext } from ".";
import dayjs from "dayjs";
import { Edit } from "@mui/icons-material";
import {
  AllCommunityModule,
  ModuleRegistry,
  ClientSideRowModelModule,
  QuickFilterModule,
  TextEditorModule,
  NumberEditorModule,
  DateFilterModule,
  themeQuartz,
  iconSetMaterial
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

ModuleRegistry.registerModules([
  AllCommunityModule,
  ClientSideRowModelModule,
  QuickFilterModule,
  TextEditorModule,
  NumberEditorModule,
  DateFilterModule,
]);

export default function ListingGrid({ handleShowButton }) {
  const rows = useContext(DataContext);
  const navigate = useNavigate();

  const gridApiRef = useRef(null);
  const columnApiRef = useRef(null);

  const [showColumnPanel, setShowColumnPanel] = useState(false);

  // State to track visibility
  const [columnVisibility, setColumnVisibility] = useState({
    role: false,
    gender: false,
  });

  const columns = useMemo(() => [
    { width: 60, filter: false, checkboxSelection: true, headerCheckboxSelection: true },
    {
      headerName: "Action",
      field: "action",
      valueGetter: () => '',
      width: 100,
      cellRenderer: (params) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            sx={{ color: "#39493F" }}
            onClick={() => navigate(`/view-details/${params.data.id}`)}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
      sortable: false,
      filter: false,
      suppressMenu: true,
    },
    { field: "code", headerName: "Employee Code", minWidth: 150 },
    { field: "name", headerName: "Full Name", minWidth: 200, flex: 1 },
    {
      field: "role",
      headerName: "Role",
      minWidth: 150,
      hide: !columnVisibility.role,
      valueFormatter: (params) => roleMap[params.value] || params.value,
    },
    { field: "email", headerName: "Email", minWidth: 200, flex: 1 },
    {
      field: "gender",
      headerName: "Gender",
      minWidth: 150,
      hide: !columnVisibility.gender,
      valueFormatter: (params) => genderMap[params.value] || params.value,
    },
    {
      field: "dob",
      headerName: "Date of Birth",
      minWidth: 150,
      flex: 1,
      filter: "agDateColumnFilter",
      valueGetter: (params) => params.value ? dayjs(params.value).toDate() : null,
      valueFormatter: (params) => params.value ? dayjs(params.value).format("DD-MM-YYYY") : "",
    },
  ], [columnVisibility, navigate]);

  const defaultColDef = useMemo(() => ({
    filter: true,
    sortable: true,
    resizable: true,
    editable: false,
  }), []);

  const myTheme = themeQuartz.withParams({
    fontFamily: 'Poppins, sans-serif',
    spacing: 12,
    accentColor: '#39493F',
    borderColor: '#BCBEC0',
    headerBackgroundColor: '#142d25ff',
    headerTextColor: '#fff'
  }).withPart(iconSetMaterial);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    columnApiRef.current = params.columnApi;
  }, []);

  // Apply column visibility whenever state changes
  useEffect(() => {
    if (!columnApiRef.current) return;
    columnApiRef.current.setColumnVisible("role", columnVisibility.role);
    columnApiRef.current.setColumnVisible("gender", columnVisibility.gender);
  }, [columnVisibility]);

  const onSelectionChanged = () => {
    if (!gridApiRef.current) return;
    const selectedRows = gridApiRef.current.getSelectedRows();
    const selectedIds = selectedRows.map(r => r.id);
    handleShowButton(selectedIds);
  };

  return (
    <div>
      <Box style={{ width: "100%", height: "75vh", overflowY: rows.length > 10 ? "auto" : "visible" }}>
        <div style={{ marginBottom: 10 }}>
          <button
            onClick={() => setShowColumnPanel(!showColumnPanel)}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "1px solid #ccc",
              background: "#142d25",
              color: "white",
              cursor: "pointer"
            }}
          >
            Columns
          </button>
        </div>

        {showColumnPanel && (
          <div
            style={{
              position: "absolute",
              zIndex: 10,
              background: "white",
              padding: 12,
              border: "1px solid #ccc",
              borderRadius: 6,
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              maxHeight: 300,
              overflowY: "auto"
            }}
          >
            {["name", "role", "gender"].map(field => (
              <div key={field} style={{ marginBottom: 4 }}>
                <input
                  type="checkbox"
                  checked={columnVisibility[field]}
                  onChange={(e) =>
                    setColumnVisibility(prev => ({
                      ...prev,
                      [field]: e.target.checked
                    }))
                  }
                />
                <label style={{ marginLeft: 6 }}>{field}</label>
              </div>
            ))}
          </div>
        )}

        <AgGridReact
          onGridReady={onGridReady}
          theme={myTheme}
          rowData={rows}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          onSelectionChanged={onSelectionChanged}
          pagination
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 25, 50]}
        />
      </Box>
    </div>
  );
}
